<div class="venn-diagram__layout">
  <svg
    [attr.viewBox]="svgViewBox()"
    preserveAspectRatio="xMidYMid meet"
    class="venn-diagram__svg"
    (mouseleave)="onRegionLeave()"
  >
    @switch (renderedSetCount()) {
      <!-- ═══════════════════════════════════════════════════ -->
      <!-- 2-SET VENN DIAGRAM                                 -->
      <!-- ═══════════════════════════════════════════════════ -->
      @case (2) {
        <defs>
          <clipPath [attr.id]="uid + '-clipA'">
            <circle [attr.cx]="cxA()" [attr.cy]="cy" [attr.r]="rA()" />
          </clipPath>
          <clipPath [attr.id]="uid + '-clipB'">
            <circle [attr.cx]="cxB()" [attr.cy]="cy" [attr.r]="rB()" />
          </clipPath>
          @if (shadingA(); as colorA) {
            <pattern
              [attr.id]="uid + '-shadeA'"
              width="10"
              height="10"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect width="10" height="10" [attr.fill]="colorA" opacity="0.15" />
              <line x1="0" y1="0" x2="0" y2="10" [attr.stroke]="colorA" stroke-width="2" />
            </pattern>
          }
          @if (shadingB(); as colorB) {
            <pattern
              [attr.id]="uid + '-shadeB'"
              width="10"
              height="10"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(-45)"
            >
              <rect width="10" height="10" [attr.fill]="colorB" opacity="0.15" />
              <line x1="0" y1="0" x2="0" y2="10" [attr.stroke]="colorB" stroke-width="2" />
            </pattern>
          }
        </defs>

        <!-- Universe background -->
        <rect
          [attr.x]="rectX"
          [attr.y]="rectY"
          [attr.width]="rectW"
          [attr.height]="rectH"
          [attr.rx]="rectRx"
          [style.fill]="outsideFill()"
          [style.opacity]="outsideOpacity()"
          class="venn-diagram__region"
          stroke="var(--venn-border)"
          stroke-width="1.5"
          (mouseenter)="onRegionEnter('outside')"
          (click)="onRegionClicked('outside')"
        />

        <!-- Circle A (onlyA) -->
        <circle
          [attr.cx]="cxA()"
          [attr.cy]="cy"
          [attr.r]="rA()"
          [style.fill]="onlyAFill()"
          [style.opacity]="onlyAOpacity()"
          class="venn-diagram__region"
          stroke="var(--venn-stroke)"
          stroke-width="1.5"
          (mouseenter)="onRegionEnter('onlyA')"
          (click)="onRegionClicked('onlyA')"
        />

        <!-- Circle B (onlyB) -->
        <circle
          [attr.cx]="cxB()"
          [attr.cy]="cy"
          [attr.r]="rB()"
          [style.fill]="onlyBFill()"
          [style.opacity]="onlyBOpacity()"
          class="venn-diagram__region"
          stroke="var(--venn-stroke)"
          stroke-width="1.5"
          (mouseenter)="onRegionEnter('onlyB')"
          (click)="onRegionClicked('onlyB')"
        />

        <!-- Intersection (A clipped to B) -->
        <circle
          [attr.cx]="cxA()"
          [attr.cy]="cy"
          [attr.r]="rA()"
          [attr.clip-path]="'url(#' + uid + '-clipB)'"
          [style.fill]="intersectionFill()"
          [style.opacity]="intersectionOpacity()"
          class="venn-diagram__region"
          stroke="none"
          (mouseenter)="onRegionEnter('intersection')"
          (click)="onRegionClicked('intersection')"
        />

        <!-- Proportional-mode tint overlays -->
        @if (propColorA(); as cA) {
          <circle
            [attr.cx]="cxA()"
            [attr.cy]="cy"
            [attr.r]="rA()"
            [attr.fill]="cA"
            stroke="none"
            pointer-events="none"
          />
        }
        @if (propColorB(); as cB) {
          <circle
            [attr.cx]="cxB()"
            [attr.cy]="cy"
            [attr.r]="rB()"
            [attr.fill]="cB"
            stroke="none"
            pointer-events="none"
          />
        }

        <!-- Solid tint overlays (static mode) -->
        @if (colorA(); as cA) {
          <circle
            [attr.cx]="cxA()"
            [attr.cy]="cy"
            [attr.r]="rA()"
            [attr.fill]="cA"
            opacity="0.15"
            stroke="none"
            pointer-events="none"
          />
        }
        @if (colorB(); as cB) {
          <circle
            [attr.cx]="cxB()"
            [attr.cy]="cy"
            [attr.r]="rB()"
            [attr.fill]="cB"
            opacity="0.15"
            stroke="none"
            pointer-events="none"
          />
        }

        <!-- Shading overlays -->
        @if (shadingA()) {
          <circle
            [attr.cx]="cxA()"
            [attr.cy]="cy"
            [attr.r]="rA()"
            [attr.fill]="'url(#' + uid + '-shadeA)'"
            stroke="none"
            pointer-events="none"
            [attr.clip-path]="shadingIntersectionOnly() ? 'url(#' + uid + '-clipB)' : null"
          />
        }
        @if (shadingB()) {
          <circle
            [attr.cx]="cxB()"
            [attr.cy]="cy"
            [attr.r]="rB()"
            [attr.fill]="'url(#' + uid + '-shadeB)'"
            stroke="none"
            pointer-events="none"
            [attr.clip-path]="shadingIntersectionOnly() ? 'url(#' + uid + '-clipA)' : null"
          />
        }

        <!-- Circle outlines -->
        <circle
          [attr.cx]="cxA()"
          [attr.cy]="cy"
          [attr.r]="rA()"
          fill="none"
          [attr.stroke]="propStrokeA() ?? strokeA()"
          stroke-width="2"
          pointer-events="none"
        />
        <circle
          [attr.cx]="cxB()"
          [attr.cy]="cy"
          [attr.r]="rB()"
          fill="none"
          [attr.stroke]="propStrokeB() ?? strokeB()"
          stroke-width="2"
          pointer-events="none"
        />

        <!-- Proportional region value labels (KaTeX) -->
        @if (isProportional()) {
          @if (showPAonly()) {
            <foreignObject
              class="venn-diagram__label-fo"
              [attr.x]="cxA() - rA() / 2 - 24"
              [attr.y]="cy - 12"
              width="48"
              height="24"
            >
              <div class="venn-diagram__region-label" [innerHTML]="pAonlyHtml()"></div>
            </foreignObject>
          }
          @if (showPAB()) {
            <foreignObject
              class="venn-diagram__label-fo"
              [attr.x]="propMidX() - 24"
              [attr.y]="cy - 12"
              width="48"
              height="24"
            >
              <div class="venn-diagram__region-label" [innerHTML]="pABhtml()"></div>
            </foreignObject>
          }
          @if (showPBonly()) {
            <foreignObject
              class="venn-diagram__label-fo"
              [attr.x]="cxB() + rB() / 2 - 24"
              [attr.y]="cy - 12"
              width="48"
              height="24"
            >
              <div class="venn-diagram__region-label" [innerHTML]="pBonlyHtml()"></div>
            </foreignObject>
          }
        }

        <!-- Labels -->
        <foreignObject
          class="venn-diagram__label-fo"
          [attr.x]="labelAx() - 24"
          [attr.y]="labelAy() - 14"
          width="48"
          height="28"
        >
          <div
            class="venn-diagram__label"
            [class.venn-diagram__label--large]="largeLabels()"
            [innerHTML]="labelAHtml()"
          ></div>
        </foreignObject>
        <foreignObject
          class="venn-diagram__label-fo"
          [attr.x]="labelBx() - 24"
          [attr.y]="labelBy() - 14"
          width="48"
          height="28"
        >
          <div
            class="venn-diagram__label"
            [class.venn-diagram__label--large]="largeLabels()"
            [innerHTML]="labelBHtml()"
          ></div>
        </foreignObject>
        <foreignObject class="venn-diagram__label-fo" [attr.x]="labelUx" [attr.y]="labelUy" width="48" height="28">
          <div
            class="venn-diagram__label venn-diagram__label--universe"
            [class.venn-diagram__label--large]="largeLabels()"
            [innerHTML]="labelUHtml()"
          ></div>
        </foreignObject>
      }

      <!-- ═══════════════════════════════════════════════════ -->
      <!-- 3-SET VENN DIAGRAM                                 -->
      <!-- Painter's algorithm: layers bottom→top so the      -->
      <!-- topmost visible layer = correct membership region. -->
      <!-- ═══════════════════════════════════════════════════ -->
      @case (3) {
        <defs>
          <clipPath [attr.id]="uid + '-clip0'">
            <circle [attr.cx]="cx3A" [attr.cy]="cy3A" [attr.r]="r3" />
          </clipPath>
          <clipPath [attr.id]="uid + '-clip1'">
            <circle [attr.cx]="cx3B" [attr.cy]="cy3B" [attr.r]="r3" />
          </clipPath>
          <clipPath [attr.id]="uid + '-clip2'">
            <circle [attr.cx]="cx3C" [attr.cy]="cy3C" [attr.r]="r3" />
          </clipPath>
        </defs>

        <!-- Layer 0: Outside all (000) -->
        <rect
          [attr.x]="rectX"
          [attr.y]="rectY"
          [attr.width]="rectW"
          [attr.height]="rectH"
          [attr.rx]="rectRx"
          [style.fill]="fill3('000')"
          [style.opacity]="opacity3('000')"
          class="venn-diagram__region"
          stroke="var(--venn-border)"
          stroke-width="1.5"
          (mouseenter)="onEnter3('000')"
          (click)="onClick3('000')"
        />

        <!-- Layer 1: Circle A — visible region = only A (100) -->
        <circle
          [attr.cx]="cx3A"
          [attr.cy]="cy3A"
          [attr.r]="r3"
          [style.fill]="fill3('100')"
          [style.opacity]="opacity3('100')"
          class="venn-diagram__region"
          stroke="var(--venn-stroke)"
          stroke-width="1.5"
          (mouseenter)="onEnter3('100')"
          (click)="onClick3('100')"
        />

        <!-- Layer 2: Circle B — visible region = only B (010) -->
        <circle
          [attr.cx]="cx3B"
          [attr.cy]="cy3B"
          [attr.r]="r3"
          [style.fill]="fill3('010')"
          [style.opacity]="opacity3('010')"
          class="venn-diagram__region"
          stroke="var(--venn-stroke)"
          stroke-width="1.5"
          (mouseenter)="onEnter3('010')"
          (click)="onClick3('010')"
        />

        <!-- Layer 3: Circle C — visible region = only C (001) -->
        <circle
          [attr.cx]="cx3C"
          [attr.cy]="cy3C"
          [attr.r]="r3"
          [style.fill]="fill3('001')"
          [style.opacity]="opacity3('001')"
          class="venn-diagram__region"
          stroke="var(--venn-stroke)"
          stroke-width="1.5"
          (mouseenter)="onEnter3('001')"
          (click)="onClick3('001')"
        />

        <!-- Layer 4: A ∩ B — visible region = A∩B only (110) -->
        <circle
          [attr.cx]="cx3A"
          [attr.cy]="cy3A"
          [attr.r]="r3"
          [attr.clip-path]="'url(#' + uid + '-clip1)'"
          [style.fill]="fill3('110')"
          [style.opacity]="opacity3('110')"
          class="venn-diagram__region"
          stroke="none"
          (mouseenter)="onEnter3('110')"
          (click)="onClick3('110')"
        />

        <!-- Layer 5: A ∩ C — visible region = A∩C only (101) -->
        <circle
          [attr.cx]="cx3A"
          [attr.cy]="cy3A"
          [attr.r]="r3"
          [attr.clip-path]="'url(#' + uid + '-clip2)'"
          [style.fill]="fill3('101')"
          [style.opacity]="opacity3('101')"
          class="venn-diagram__region"
          stroke="none"
          (mouseenter)="onEnter3('101')"
          (click)="onClick3('101')"
        />

        <!-- Layer 6: B ∩ C — visible region = B∩C only (011) -->
        <circle
          [attr.cx]="cx3B"
          [attr.cy]="cy3B"
          [attr.r]="r3"
          [attr.clip-path]="'url(#' + uid + '-clip2)'"
          [style.fill]="fill3('011')"
          [style.opacity]="opacity3('011')"
          class="venn-diagram__region"
          stroke="none"
          (mouseenter)="onEnter3('011')"
          (click)="onClick3('011')"
        />

        <!-- Layer 7: A ∩ B ∩ C — visible region = triple intersection (111) -->
        <g [attr.clip-path]="'url(#' + uid + '-clip2)'">
          <circle
            [attr.cx]="cx3A"
            [attr.cy]="cy3A"
            [attr.r]="r3"
            [attr.clip-path]="'url(#' + uid + '-clip1)'"
            [style.fill]="fill3('111')"
            [style.opacity]="opacity3('111')"
            class="venn-diagram__region"
            stroke="none"
            (mouseenter)="onEnter3('111')"
            (click)="onClick3('111')"
          />
        </g>

        <!-- Circle outlines (drawn on top, non-interactive) -->
        <circle
          [attr.cx]="cx3A"
          [attr.cy]="cy3A"
          [attr.r]="r3"
          fill="none"
          stroke="var(--venn-stroke)"
          stroke-width="1.5"
          pointer-events="none"
        />
        <circle
          [attr.cx]="cx3B"
          [attr.cy]="cy3B"
          [attr.r]="r3"
          fill="none"
          stroke="var(--venn-stroke)"
          stroke-width="1.5"
          pointer-events="none"
        />
        <circle
          [attr.cx]="cx3C"
          [attr.cy]="cy3C"
          [attr.r]="r3"
          fill="none"
          stroke="var(--venn-stroke)"
          stroke-width="1.5"
          pointer-events="none"
        />

        <!-- Labels -->
        <foreignObject
          class="venn-diagram__label-fo"
          [attr.x]="label3Ax - 24"
          [attr.y]="label3Ay - 14"
          width="48"
          height="28"
        >
          <div
            class="venn-diagram__label"
            [class.venn-diagram__label--large]="largeLabels()"
            [innerHTML]="labelAHtml()"
          ></div>
        </foreignObject>
        <foreignObject
          class="venn-diagram__label-fo"
          [attr.x]="label3Bx - 24"
          [attr.y]="label3By - 14"
          width="48"
          height="28"
        >
          <div
            class="venn-diagram__label"
            [class.venn-diagram__label--large]="largeLabels()"
            [innerHTML]="labelBHtml()"
          ></div>
        </foreignObject>
        <foreignObject
          class="venn-diagram__label-fo"
          [attr.x]="label3Cx - 24"
          [attr.y]="label3Cy - 14"
          width="48"
          height="28"
        >
          <div
            class="venn-diagram__label"
            [class.venn-diagram__label--large]="largeLabels()"
            [innerHTML]="labelCHtml()"
          ></div>
        </foreignObject>
        <foreignObject class="venn-diagram__label-fo" [attr.x]="labelUx" [attr.y]="labelUy" width="48" height="28">
          <div
            class="venn-diagram__label venn-diagram__label--universe"
            [class.venn-diagram__label--large]="largeLabels()"
            [innerHTML]="labelUHtml()"
          ></div>
        </foreignObject>
      }
    }
  </svg>

  @if (isInteractive()) {
    <div class="venn-diagram__right-panel">
      <div class="venn-diagram__sliders">
        <div class="venn-diagram__section-header">Probabilities</div>
        <div class="venn-diagram__slider-row">
          <div class="venn-diagram__slider-label" [innerHTML]="sliderLabelPA()"></div>
          <dojo-slider
            param=""
            [showLabel]="false"
            variant="minimal"
            [min]="0"
            [max]="1"
            [step]="0.01"
            [value]="pA()"
            [editable]="true"
            color="orange"
            (valueChange)="onPAChange($event)"
          />
        </div>
        <div class="venn-diagram__slider-row">
          <div class="venn-diagram__slider-label" [innerHTML]="sliderLabelPB()"></div>
          <dojo-slider
            param=""
            [showLabel]="false"
            variant="minimal"
            [min]="0"
            [max]="1"
            [step]="0.01"
            [value]="pB()"
            [editable]="true"
            color="indigo"
            (valueChange)="onPBChange($event)"
          />
        </div>
        <div class="venn-diagram__slider-row">
          <div class="venn-diagram__slider-label" [innerHTML]="sliderLabelPAB()"></div>
          <dojo-slider
            param=""
            [showLabel]="false"
            variant="minimal"
            [min]="minOverlap()"
            [max]="maxOverlap()"
            [step]="0.01"
            [value]="pAB()"
            [editable]="true"
            color="neutral"
            (valueChange)="onPABChange($event)"
          />
        </div>
      </div>
      <div class="venn-diagram__formulas">
        <div class="venn-diagram__section-header">Conclusion</div>
        <div class="venn-diagram__formula-row" [innerHTML]="formulaAgivenB()"></div>
        <div class="venn-diagram__formula-row" [innerHTML]="formulaBgivenA()"></div>
        <div class="venn-diagram__formula-row" [innerHTML]="formulaIndep()"></div>
      </div>
    </div>
  }
</div>
